import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { rewardFullModal } from './styles';

import { useRewardfulService } from './rewardful.service';
import { Modal, Button, Typography, CircleLoading } from 'shared';

//icon
const DollarSignIcon = () => (
    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8.67188 14.3298C8.67188 15.6198 9.66188 16.6598 10.8919 16.6598H13.4019C14.4719 16.6598 15.3419 15.7498 15.3419 14.6298C15.3419 13.4098 14.8119 12.9798 14.0219 12.6998L9.99187 11.2998C9.20187 11.0198 8.67188 10.5898 8.67188 9.36984C8.67188 8.24984 9.54187 7.33984 10.6119 7.33984H13.1219C14.3519 7.33984 15.3419 8.37984 15.3419 9.66984"
            stroke="#0061DB"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 6V18"
            stroke="#0061DB"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
            stroke="#0061DB"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const InviteUserLinkModal = ({ open, handleCloseModal, handleExclusionRuleCreationSuccess }) => {
    const classes = rewardFullModal();
    const [error, setError] = useState(null);
    const [isCopied, setIsCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [affiliateToken, setAffiliateToken] = useState(null);
    const companyData = useSelector((state) => state.company.data);
    const userEmail = useSelector((state) => state.auth.data.user_email);

    const { fetchAffiliateToken, createAffiliateToken } = useRewardfulService();
    const baseUrl = process.env.REACT_APP_CIRCLER_API_URL;
    const affiliateLink = affiliateToken ? `${baseUrl}?via=${affiliateToken}` : null;

    useEffect(() => {
        let isMounted = true;

        if (!open) return;

        const getAffiliateToken = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const res = await fetchAffiliateToken(companyData?.id);
                if (isMounted) {
                    setAffiliateToken(res.affiliate_token || null);
                }
            } catch (err) {
                if (isMounted) {
                    if (err.response?.status !== 404) {
                        setError('Failed to check for existing affiliate link.');
                    }
                    setAffiliateToken(null);
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        getAffiliateToken();

        return () => {
            isMounted = false;
        };
    }, [open]);

    const handleCreateAffiliateToken = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await createAffiliateToken({
                companyId: companyData?.id,
                email: userEmail,
                first_name: companyData?.name,
                last_name: companyData?.name,
            });
            setAffiliateToken(res.affiliate_token);
            handleExclusionRuleCreationSuccess?.();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create affiliate link.');
        } finally {
            setIsLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard
            .writeText(affiliateLink)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch(() => setError('Copy failed. Please copy manually.'));
    };

    return (
        <Modal
            fullWidth
            open={open}
            title="Share Your Affiliate Link"
            handleClose={handleCloseModal}
        >
            <Box sx={classes.root}>
                <Box sx={classes.iconWrapper}>
                    <DollarSignIcon />
                </Box>

                <Typography
                    variant="body1"
                    sx={classes.body1}
                    gutterBottom
                    text={
                        'Refer your friends and earn 10% commission for 12 months on every referral.'
                    }
                ></Typography>
                <Typography
                    variant="body2"
                    sx={classes.body1}
                    gutterBottom
                    text={'They sign up, you get paid — simple as that.'}
                ></Typography>
                <Typography
                    variant="subtitle2"
                    sx={classes.subtitle2}
                    text={'Copy your link, share it, and start earning today!'}
                ></Typography>
            </Box>

            {isLoading ? (
                <Box sx={classes.loadingContainer}>
                    <CircleLoading />
                </Box>
            ) : error ? (
                <Box sx={classes.errorContainer}>
                    <Typography variant="body1" color="error" gutterBottom>
                        {error}
                    </Typography>
                    <Button variant="contained" onClick={handleCloseModal} fullWidth text="Close" />
                </Box>
            ) : affiliateToken ? (
                <Box px={3} py={2}>
                    <Typography variant="body1" sx={classes.affiliateInfo}>
                        Your affiliate link is ready to share:
                    </Typography>
                    <Box sx={classes.affiliateContainer}>
                        <Box sx={classes.affiliateLinkText}>{affiliateLink}</Box>
                        <Tooltip title={isCopied ? 'Copied!' : 'Copy to clipboard'}>
                            <IconButton
                                onClick={copyToClipboard}
                                color={isCopied ? 'success' : 'primary'}
                                size="small"
                            >
                                {isCopied ? (
                                    <CheckIcon fontSize="small" />
                                ) : (
                                    <ContentCopyIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Button variant="contained" onClick={handleCloseModal} fullWidth text="Done" />
                </Box>
            ) : (
                <Box px={3} py={2} textAlign="center">
                    <Typography variant="body1" gutterBottom>
                        You don't have an affiliate link yet.
                    </Typography>
                    <Typography variant="body2" sx={classes.body1} gutterBottom>
                        Create one to start sharing and earning!
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleCreateAffiliateToken}
                        fullWidth
                        loading={isLoading}
                        text="Create Affiliate Link"
                    />
                </Box>
            )}
        </Modal>
    );
};

export default InviteUserLinkModal;
